import Bugsnag from '@bugsnag/js';
import { Button, CircularProgress } from '@civilgrid/ui/components';
import { kml } from '@tmcw/togeojson';
import {
  bbox,
  booleanValid,
  feature,
  featureCollection,
  flatten,
  lineString,
  multiLineString,
  polygon,
  truncate,
  union,
} from '@turf/turf';
import type { Feature, Geometry, LineString, MultiPolygon, Polygon } from 'geojson';
import JSZip from 'jszip';
import { nanoid } from 'nanoid';
import { useMemo, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import FeatureFlags, { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { isValidKMZProjectFeature, type ValidKMZProjectGeometry } from '~/common/kmzUtils';
import { captureTypedPosthogEvent, KmzUploadErrorReason, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { ProjectGeometryType } from '~/common/types';
import UploadButton from '~/components2/forms/UploadButton';
import { homeMapStore as store } from '~/pages/homeViewV2/store';
import rootStore from '~/store';
import { errors, type KmzUploadError } from './kmzUploadErrors';

const DEFAULT_BUFFER = 150; // in feet
const MAX_SEGMENTS = 25;

const createPolygonSegment = (geometry: Polygon, index: number) => {
  store.newObject({
    id: nanoid(),
    sortOrder: 'a1',
    type: 'polygon',
    title: `Polygon ${index + 1}`,
    description: '',
    geometry,
    properties: {
      color: 'red',
      fillOpacity: 0.25,
      strokeOpacity: 1,
      strokeWidth: 4,
      strokeStyle: 'solid',
      showArea: true,
      showTitle: true,
      labelAlwaysVisible: false,
    },
  });
};

const createLineSegment = (geometry: LineString, index: number) => {
  store.newObject({
    id: nanoid(),
    sortOrder: 'a1',
    type: 'line',
    title: `Line ${index + 1}`,
    description: '',
    geometry,
    properties: {
      color: 'red',
      buffer: DEFAULT_BUFFER,
      strokeWidth: 4,
      strokeOpacity: 1,
      strokeStyle: 'solid',
      showLength: true,
      showTitle: true,
      labelAlwaysVisible: false,
    },
  });
};

type UploadStep = 'select' | 'processing' | 'error' | 'confirm';

export default function UploadKMZProject() {
  const { homeMap } = useMap();
  const { currentOrganizationDetail, me } = useSnapshot(rootStore);
  const [step, setStep] = useState<UploadStep>('select');
  const [error, setError] = useState<string | undefined>();
  const [showStartDraft, setShowStartDraft] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [validFeatures, setValidFeatures] = useState<Feature<ValidKMZProjectGeometry>[]>([]);
  const [invalidFeatures, setInvalidFeatures] = useState<Feature<Geometry | null>[]>([]);
  const organizationId = currentOrganizationDetail.isLoading ? undefined : currentOrganizationDetail.id;

  const handleStartDraft = () => {
    if (currentOrganizationDetail.isLoading || !me) {
      return;
    }

    const mailBody = `Hi CivilGrid,

Please create a project draft from the attached KMZ.

Project Name: [Please Fill This Out]
Org: ${currentOrganizationDetail.name}
PM Email: ${me.email}

Note: CivilGrid will notify you once your draft has been created. You can easily upgrade it to a full project whenever you’re ready.
`;

    const mailtoLink = `mailto:support@civilgrid.com?subject=${encodeURIComponent(
      `Create project from KMZ - ${currentOrganizationDetail.name}`,
    )}&body=${encodeURIComponent(mailBody)}`;

    window.open(mailtoLink);
  };

  const handleError = (uploadError: KmzUploadError, startDraft: boolean = false, errorDetails?: Error) => {
    setStep('error');
    setError(uploadError.message);

    if (startDraft) {
      setShowStartDraft(true);
    }

    captureTypedPosthogEvent(POSTHOG_EVENTS.projectBoundaryKMZUploadFailed, {
      organization_id: organizationId,
      error_reason: uploadError.reason,
      error_message: uploadError.message,
      error_details: errorDetails?.message,
    });

    if (uploadError.reason === KmzUploadErrorReason.General && errorDetails) {
      // If we are in the general case it means we didn't account for this kind of error,
      // so it might be an actual bug and is worth throwing
      Bugsnag.notify(
        new Error(`Unhandled error case when uploading project KMZ: ${errorDetails.message}`, { cause: errorDetails }),
      );
    }
  };

  const getMaxSegments = () => {
    if (FeatureFlags.hasOrgFeatureFlag(OrgFeatureFlag.ProjectSegmentLimit100)) {
      return 100;
    } else {
      return MAX_SEGMENTS;
    }
  };
  const exceedsMax = (count: number) => {
    return count > getMaxSegments();
  };

  const handleReset = () => {
    setFile(null);
    setStep('select');
    setShowStartDraft(false);
    setValidFeatures([]);
    setInvalidFeatures([]);
    setError(undefined);
    store.resetCurrentObjectId();
    store.resetObjects();
  };

  const onUpload = async (files: File[]) => {
    try {
      handleReset();
      setStep('processing');

      const kmzFile = files[0];
      if (!kmzFile) {
        return handleError(errors.missingFile);
      }

      const reader = new FileReader();
      reader.readAsText(kmzFile);

      reader.onload = async (e) => {
        let kmlContent: string | undefined;

        // its a zipped file
        if (kmzFile.name.endsWith('.kmz')) {
          const zip = await JSZip.loadAsync(kmzFile);
          kmlContent = await zip.file('doc.kml')?.async('text');
        } else {
          kmlContent = e.target?.result?.toString();
        }

        if (!kmlContent) {
          return handleError(errors.noContent);
        }

        const kmlData = new DOMParser().parseFromString(kmlContent, 'text/xml');
        const geojson = kml(kmlData);

        const _invalid: Feature<Geometry | null>[] = [];
        const features: Feature<ValidKMZProjectGeometry>[] = [];

        geojson.features.forEach((f) => {
          if (f.geometry?.type === 'GeometryCollection') {
            f.geometry.geometries.forEach((g) => {
              const geomAsFeature = feature(g);
              if (isValidKMZProjectFeature(geomAsFeature)) {
                features.push(truncate(geomAsFeature, { coordinates: 2, precision: 7 }));
              } else {
                _invalid.push(geomAsFeature);
              }
            });
          } else {
            if (isValidKMZProjectFeature(f)) {
              features.push(truncate(f, { coordinates: 2, precision: 7 }));
            } else {
              _invalid.push(f);
            }
          }
        });

        setValidFeatures(features);
        setInvalidFeatures(_invalid);

        // if no good features
        if (!features.length) {
          return handleError(errors.noFeatures);
        }

        const type = features.find((f) => !!f.geometry?.type)?.geometry?.type;
        if (!type) return handleError(errors.invalidGeometryType);

        const isAllSameGeometryType = features.every((f) => f.geometry?.type === type);

        if (!isAllSameGeometryType) {
          return handleError(errors.mixedGeometryTypes, true);
        }

        if (type.includes('Polygon')) {
          store.setGeometryType(ProjectGeometryType.Polygon);
          // flatten to polygon feature collection
          const flatFeatures = flatten(featureCollection(features));
          // unionize them together into a single multi-polygon
          let unionized: Feature<Polygon | MultiPolygon> | null = null;

          // If more than 1 feature, union them. Otherwise, return the single feature.
          if (flatFeatures.features.length > 1) {
            unionized = union(flatFeatures);
          } else {
            unionized = flatFeatures.features[0];
          }

          if (!unionized || !booleanValid(unionized)) {
            return handleError(errors.invalidPolygonGeometry);
          }

          if (unionized.geometry.type === 'MultiPolygon') {
            const segmentCount = unionized.geometry.coordinates.length;
            // if more than max features
            if (exceedsMax(segmentCount)) {
              return handleError(errors.tooManySegments(segmentCount, getMaxSegments()));
            }

            unionized.geometry.coordinates.forEach((ring, i) => {
              createPolygonSegment(polygon([ring[0]]).geometry, i);
            });
          } else {
            createPolygonSegment(unionized.geometry, 0);
          }
        } else if (type.includes('LineString')) {
          store.setGeometryType(ProjectGeometryType.Linear);
          store.updateLineBuffer(DEFAULT_BUFFER);

          const flatFeatures = flatten(featureCollection(features));

          // validating each flattened feature individually before combining into MultiLineString to dodge a turf.js bug (it considers MultiLineString with a single line to be invalid)
          if (!flatFeatures.features.every((f) => booleanValid(f))) {
            return handleError(errors.invalidLineStringGeometry);
          }

          const combinedLines = multiLineString(flatFeatures.features.map((f) => f.geometry.coordinates));

          // if more than max features
          const segmentCount = combinedLines.geometry.coordinates.length;
          if (exceedsMax(segmentCount)) {
            return handleError(errors.tooManySegments(segmentCount, getMaxSegments()));
          }

          combinedLines.geometry.coordinates.forEach((l, i) => {
            createLineSegment(lineString(l).geometry, i);
          });
        } else {
          return handleError(errors.invalidGeometryType);
        }

        const bounds = bbox(featureCollection(features));
        homeMap?.fitBounds(bounds as any, { padding: 100, speed: 2 });
        store.resetCurrentObjectId();
        setFile(kmzFile);
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectBoundaryKMZUploaded, { organization_id: organizationId });
        setStep('confirm');
      };
    } catch (e) {
      handleError(errors.general, false, e as Error);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (step === 'select') {
      return (
        <div>
          <div className="text-sm font-noto">
            💡 Upload a KML or KMZ file containing either <span className="font-semibold">lines</span> or{' '}
            <span className="font-semibold">polygons</span> — mixed geometries are not supported. Your file may contain
            up to {getMaxSegments()} segments.
          </div>
          <br />
          <UploadButton accept={['.kmz', '.kml']} onUpload={onUpload} alternate={!!file}>
            {file ? 'Reset' : 'Select KMZ File'}
          </UploadButton>
        </div>
      );
    } else if (step === 'processing') {
      return (
        <div className="flex gap-2">
          <CircularProgress indeterminate /> Processing your KMZ/KML file...
        </div>
      );
    } else if (step === 'error') {
      return (
        <div className="space-y-4">
          {!!error && <div>⚠️ {error}</div>}

          <div className="space-y-4">
            {!!showStartDraft && (
              <div>
                To create a project from this KMZ/KML file, click "Start Draft" below to send the file to
                support@civilgrid.com
              </div>
            )}

            <div className="flex w-full items-center justify-between">
              <ResetButton onReset={handleReset} />

              {!!showStartDraft && (
                <Button onClick={handleStartDraft} data-testid="kmz-start-draft-button">
                  Start Draft
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    } else if (step === 'confirm') {
      return (
        <div className="flex w-full flex-col space-y-4">
          <div>
            {validFeatures.length} segment(s) automatically imported, proceed if everything looks correct on the map.
          </div>

          {!!invalidFeatures.length && (
            <div className="">
              ⚠️ Unable to parse {invalidFeatures.length} segment(s) from the KMZ/KML file. You can still proceed with
              the import, but some segments may not be visible in the map.
              <br />
              <br />
              <div className="font-semibold">Invalid segments:</div>
              <ul className="list-inside list-disc">
                {invalidFeatures.map((f, i) => {
                  return (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                      key={`invalid-feature-${i}`}
                    >
                      {f.properties?.name || 'Untitled Path'}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex w-full items-center justify-between">
            <ResetButton onReset={handleReset} />

            <Button
              data-testid="kmz-add-project-details-button"
              onClick={() => {
                store.setInUploadKMZMode(false);
                store.setIsGeometryFromKMZ(true);
                store.setKmzFileName(file?.name.replace(/\.(kmz|kml)$/i, ''));
              }}
            >
              Add Project Details
            </Button>
          </div>
        </div>
      );
    }
  }, [step, error]);

  return (
    <div className="space-y-4">
      <div>{content}</div>
    </div>
  );
}

function ResetButton({ onReset }: { onReset: () => void }) {
  return (
    <Button onClick={onReset} variant="secondary" data-testid="kmz-restart-button">
      Restart
    </Button>
  );
}
